/**
 * Shared per-band bandpass + correlation helper.
 *
 * Several downstream analyses (spectral signature, per-band envelopes) all
 * need the same artefact: for each frequency band, a bandpass-filtered mic
 * signal cross-correlated against the bandpass-filtered reference. Computing
 * that artefact independently in each consumer means 2× the FFT-correlate
 * work per band per ping. This module produces it exactly once so consumers
 * can share it.
 *
 * Pure-function module — no I/O, no global state.
 */

import type { BandConfig } from '../types.js';
import { MULTIBAND_BANDS, MULTIBAND_BAND_L } from '../constants.js';
import { applyBandpass, designBandpass, type BandpassCoeffs } from '../dsp/bandpass.js';
import { fftCorrelateComplex } from '../dsp/fft-correlate.js';

/**
 * FIR design is deterministic in (fLow, fHigh, sampleRate, taps) but costs
 * O(taps²)-ish work per call; the bands are module constants, so per-ping
 * redesign was pure waste. Steady state holds one entry per band (~3).
 */
const coeffsCache = new Map<string, BandpassCoeffs>();

function getBandpassCoeffs(
  fLow: number,
  fHigh: number,
  sampleRate: number,
  numTaps: number,
): BandpassCoeffs {
  const key = `${fLow}|${fHigh}|${sampleRate}|${numTaps}`;
  let coeffs = coeffsCache.get(key);
  if (!coeffs) {
    coeffs = designBandpass(fLow, fHigh, sampleRate, numTaps);
    coeffsCache.set(key, coeffs);
  }
  return coeffs;
}

/** A single band's bandpassed signals + correlation. */
export interface BandedCorrelation {
  /** Band identifier (e.g. 'L', 'M', 'H'). */
  id: string;
  /** Lower cutoff (Hz). */
  fLow: number;
  /** Upper cutoff (Hz). */
  fHigh: number;
  /** Real part of the cross-correlation (matches `CorrelationResult.correlation`). */
  corrReal: Float32Array;
  /**
   * Imaginary part of the analytic cross-correlation (the Hilbert transform
   * of `corrReal`, from `fftCorrelateComplex`). Downstream envelope analysis
   * uses it directly instead of recomputing a Hilbert transform per band.
   * Null only in hand-built test fixtures.
   */
  corrImag: Float32Array | null;
  /** Bandpassed mic capture (length = micWin.length). */
  micFiltered: Float32Array;
  /** Bandpassed reference signal (length = ref.length). */
  refFiltered: Float32Array;
}

/** Arguments for {@link computeBandCorrelations}. */
export interface ComputeBandCorrelationsArgs {
  /** Raw unfiltered mic capture (post-cropping to listen window). */
  micWin: Float32Array;
  /** Reference signal (the original chirp/MLS/Golay/etc). */
  ref: Float32Array;
  /** Sample rate (Hz). */
  sampleRate: number;
  /** Default: [MULTIBAND_BAND_L, ...MULTIBAND_BANDS]. */
  bands?: BandConfig[];
}

/**
 * For each band, design FIR coeffs once, filter mic and ref, cross-correlate,
 * and return the resulting artefacts.
 *
 * Returns `[]` for degenerate inputs (empty arrays, mic shorter than ref, or
 * non-positive sampleRate). Conventions match `spectral-signature.ts` /
 * `per-band-envelope.ts` — the helper they previously duplicated.
 */
export function computeBandCorrelations(
  args: ComputeBandCorrelationsArgs,
): BandedCorrelation[] {
  const {
    micWin,
    ref,
    sampleRate,
    bands = [MULTIBAND_BAND_L, ...MULTIBAND_BANDS],
  } = args;

  if (micWin.length === 0 || ref.length === 0) return [];
  if (micWin.length < ref.length) return [];
  if (!(sampleRate > 0 && Number.isFinite(sampleRate))) return [];

  const out: BandedCorrelation[] = [];
  for (let i = 0; i < bands.length; i++) {
    const band = bands[i];
    const coeffs = getBandpassCoeffs(
      band.fLow,
      band.fHigh,
      sampleRate,
      band.filterTaps,
    );
    const micFiltered = applyBandpass(micWin, coeffs);
    const refFiltered = applyBandpass(ref, coeffs);
    const corrResult = fftCorrelateComplex(micFiltered, refFiltered, sampleRate);
    out.push({
      id: band.id,
      fLow: band.fLow,
      fHigh: band.fHigh,
      corrReal: corrResult.correlation,
      corrImag: corrResult.correlationImag,
      micFiltered,
      refFiltered,
    });
  }
  return out;
}
